import React, {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react'

import Footer from '../components/Footer'
import Header from '../components/Header'

import {
    setOptions,
    importLibrary
} from '@googlemaps/js-api-loader'

import { APIConstants } from '../Helpers/Constants'
import Loader from '../components/Loader'
import PricingModal from '../components/PricingModal'
import axios from 'axios'
import { ImageConstant } from '../images'


const GOOGLE_MAPS_API_KEY = "AIzaSyBDaow06obJbZ901UQ9WuQQpC-Eo_3FP5E"


/*
|--------------------------------------------------------------------------
| Canadian Postal Code Autocomplete
|--------------------------------------------------------------------------
*/

function PostalCodeInput({
    placeholder,
    onSelect
}) {

    const containerRef = useRef(null)

    useEffect(() => {

        let mounted = true

        let autocomplete = null


        const initializeAutocomplete = async () => {

            try {

                /*
                |--------------------------------------------------------------------------
                | Configure Google Maps
                |--------------------------------------------------------------------------
                */

                setOptions({
                    key: GOOGLE_MAPS_API_KEY,
                    v: 'weekly'
                })


                /*
                |--------------------------------------------------------------------------
                | Load Places library
                |--------------------------------------------------------------------------
                */

                const {
                    PlaceAutocompleteElement
                } = await importLibrary('places')


                if (
                    !mounted ||
                    !containerRef.current
                ) {
                    return
                }


                /*
                |--------------------------------------------------------------------------
                | Create autocomplete
                |--------------------------------------------------------------------------
                */

                autocomplete =
                    new PlaceAutocompleteElement()


                /*
                |--------------------------------------------------------------------------
                | Canada only
                |--------------------------------------------------------------------------
                */

                autocomplete.includedRegionCodes = [
                    'ca'
                ]


                /*
                |--------------------------------------------------------------------------
                | Regions = postal codes, cities, etc.
                |--------------------------------------------------------------------------
                */

                autocomplete.includedPrimaryTypes = [
                    '(regions)'
                ]


                /*
                |--------------------------------------------------------------------------
                | Placeholder
                |--------------------------------------------------------------------------
                */

                autocomplete.placeholder =
                    placeholder


                /*
                |--------------------------------------------------------------------------
                | Width
                |--------------------------------------------------------------------------
                */

                autocomplete.style.width =
                    '100%'


                /*
                |--------------------------------------------------------------------------
                | Handle selection
                |--------------------------------------------------------------------------
                */

                autocomplete.addEventListener(
                    'gmp-select',
                    async (event) => {

                        try {

                            const placePrediction =
                                event.placePrediction


                            if (!placePrediction) {
                                return
                            }


                            /*
                            |--------------------------------------------------------------------------
                            | Convert prediction to Place
                            |--------------------------------------------------------------------------
                            */

                            const place =
                                placePrediction.toPlace()


                            /*
                            |--------------------------------------------------------------------------
                            | Only request the fields we need
                            |--------------------------------------------------------------------------
                            */

                            await place.fetchFields({

                                fields: [
                                    'addressComponents',
                                    'location'
                                ]

                            })


                            /*
                            |--------------------------------------------------------------------------
                            | Extract postal code
                            |--------------------------------------------------------------------------
                            */

                            let postalCode = ''


                            place.addressComponents?.forEach(
                                (component) => {

                                    if (
                                        component.types?.includes(
                                            'postal_code'
                                        )
                                    ) {

                                        postalCode =
                                            component.longText || ''

                                    }

                                }
                            )


                            /*
                            |--------------------------------------------------------------------------
                            | Make sure a postal code was found
                            |--------------------------------------------------------------------------
                            */

                            if (!postalCode) {

                                alert(
                                    'Please select a valid Canadian postal code.'
                                )

                                return
                            }


                            /*
                            |--------------------------------------------------------------------------
                            | Coordinates
                            |--------------------------------------------------------------------------
                            */

                            const lat =
                                place.location
                                    ? place.location.lat()
                                    : ''


                            const long =
                                place.location
                                    ? place.location.lng()
                                    : ''


                            /*
                            |--------------------------------------------------------------------------
                            | IMPORTANT:
                            |
                            | We only return postalCode,
                            | lat and long.
                            |
                            | We DO NOT store:
                            |
                            | Etobicoke, ON M9W 0A3, Canada
                            |--------------------------------------------------------------------------
                            */

                            const data = {

                                postalCode,

                                lat,

                                long

                            }


                            console.log(
                                'Selected postal code:',
                                postalCode
                            )


                            console.log(
                                'Selected location:',
                                data
                            )


                            /*
                            |--------------------------------------------------------------------------
                            | Send data to parent
                            |--------------------------------------------------------------------------
                            */

                            onSelect(data)


                        } catch (error) {

                            console.error(
                                'Postal code selection error:',
                                error
                            )

                        }

                    }
                )


                /*
                |--------------------------------------------------------------------------
                | Add Google element
                |--------------------------------------------------------------------------
                */

                containerRef.current.innerHTML = ''

                containerRef.current.appendChild(
                    autocomplete
                )


            } catch (error) {

                console.error(
                    'Google Maps initialization error:',
                    error
                )

            }

        }


        initializeAutocomplete()


        /*
        |--------------------------------------------------------------------------
        | Cleanup
        |--------------------------------------------------------------------------
        */

        return () => {

            mounted = false

            if (containerRef.current) {

                containerRef.current.innerHTML = ''

            }

        }

    }, [
        placeholder,
        onSelect
    ])


    return (

        <div
            ref={containerRef}
            style={{
                width: '100%',
                minHeight: '40px'
            }}
        />

    )

}


/*
|--------------------------------------------------------------------------
| Pricing Page
|--------------------------------------------------------------------------
*/

export default function Pricing() {


    /*
    |--------------------------------------------------------------------------
    | Package
    |--------------------------------------------------------------------------
    */

    const [packageData, setPackageData] =
        useState({

            packageType: '',

            weight: ''

        })


    /*
    |--------------------------------------------------------------------------
    | Pickup
    |--------------------------------------------------------------------------
    */

    const [pickupData, setPickupData] =
        useState({

            postalCode: '',

            lat: '',

            long: ''

        })


    /*
    |--------------------------------------------------------------------------
    | Destination
    |--------------------------------------------------------------------------
    */

    const [destinationData, setDestinationData] =
        useState({

            postalCode: '',

            lat: '',

            long: ''

        })


    /*
    |--------------------------------------------------------------------------
    | Other states
    |--------------------------------------------------------------------------
    */

    const [allPackages, setAllPackages] =
        useState([])


    const [distance, setDistance] =
        useState('')


    const [showModal, setShowModal] =
        useState(false)


    const [showLoading, setShowLoading] =
        useState(false)


    /*
    |--------------------------------------------------------------------------
    | Debug
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        console.log(
            'Pickup:',
            pickupData
        )

        console.log(
            'Destination:',
            destinationData
        )

    }, [
        pickupData,
        destinationData
    ])


    /*
    |--------------------------------------------------------------------------
    | Pickup selection
    |--------------------------------------------------------------------------
    */

    const handlePickupSelect =
        useCallback((data) => {

            setPickupData(data)

        }, [])


    /*
    |--------------------------------------------------------------------------
    | Destination selection
    |--------------------------------------------------------------------------
    */

    const handleDestinationSelect =
        useCallback((data) => {

            setDestinationData(data)

        }, [])


    /*
    |--------------------------------------------------------------------------
    | Convert degrees to radians
    |--------------------------------------------------------------------------
    */

    function toRad(value) {

        return value *
            Math.PI /
            180

    }


    /*
    |--------------------------------------------------------------------------
    | Calculate distance
    |--------------------------------------------------------------------------
    */

    function calcCrow(
        lat1,
        lon1,
        lat2,
        lon2
    ) {

        const R = 6371


        const dLat =
            toRad(
                lat2 - lat1
            )


        const dLon =
            toRad(
                lon2 - lon1
            )


        lat1 =
            toRad(lat1)


        lat2 =
            toRad(lat2)


        const a =

            Math.sin(dLat / 2) *
            Math.sin(dLat / 2)

            +

            Math.sin(dLon / 2) *
            Math.sin(dLon / 2) *
            Math.cos(lat1) *
            Math.cos(lat2)


        const c =
            2 *
            Math.atan2(
                Math.sqrt(a),
                Math.sqrt(1 - a)
            )


        const d =
            R * c


        console.log(
            'Distance:',
            d
        )


        showDateRequest(d)


        return d

    }


    /*
    |--------------------------------------------------------------------------
    | Get Estimate
    |--------------------------------------------------------------------------
    */

    const reqDistance = (event) => {

        event.preventDefault()


        /*
        |--------------------------------------------------------------------------
        | Pickup validation
        |--------------------------------------------------------------------------
        */

        if (
            !pickupData.postalCode ||
            !pickupData.lat ||
            !pickupData.long
        ) {

            alert(
                'Please select a valid pickup postal code.'
            )

            return

        }


        /*
        |--------------------------------------------------------------------------
        | Destination validation
        |--------------------------------------------------------------------------
        */

        if (
            !destinationData.postalCode ||
            !destinationData.lat ||
            !destinationData.long
        ) {

            alert(
                'Please select a valid destination postal code.'
            )

            return

        }


        /*
        |--------------------------------------------------------------------------
        | Weight validation
        |--------------------------------------------------------------------------
        */

        if (!packageData.weight) {

            alert(
                'Please enter valid weight.'
            )

            return

        }


        /*
        |--------------------------------------------------------------------------
        | Calculate distance
        |--------------------------------------------------------------------------
        */

        calcCrow(

            pickupData.lat,
            pickupData.long,

            destinationData.lat,
            destinationData.long

        )

    }


    /*
    |--------------------------------------------------------------------------
    | Price estimation
    |--------------------------------------------------------------------------
    */

    const showDateRequest =
        async (calculatedDistance) => {

            const config = {
                headers:
                    APIConstants.token
            }


            setShowLoading(true)


            try {

                /*
                |--------------------------------------------------------------------------
                | API data
                |--------------------------------------------------------------------------
                */

                const data = {

                    /*
                    |--------------------------------------------------------------------------
                    | ONLY postal codes
                    |--------------------------------------------------------------------------
                    */

                    from:
                        pickupData.postalCode,

                    to:
                        destinationData.postalCode,


                    pickup_postal_code:
                        pickupData.postalCode,

                    destination_postal_code:
                        destinationData.postalCode,


                    /*
                    |--------------------------------------------------------------------------
                    | Coordinates
                    |--------------------------------------------------------------------------
                    */

                    pickup_lat:
                        pickupData.lat,

                    pickup_long:
                        pickupData.long,


                    destination_lat:
                        destinationData.lat,

                    destination_long:
                        destinationData.long,


                    /*
                    |--------------------------------------------------------------------------
                    | Package
                    |--------------------------------------------------------------------------
                    */

                    weight:
                        packageData.weight,


                    /*
                    |--------------------------------------------------------------------------
                    | OPTIONAL
                    |--------------------------------------------------------------------------
                    */

                    package_type:
                        packageData.packageType,


                    /*
                    |--------------------------------------------------------------------------
                    | Distance
                    |--------------------------------------------------------------------------
                    */

                    distance:
                        calculatedDistance

                }


                console.log(
                    'Price estimation request:',
                    data
                )


                const response =
                    await axios.post(

                        `${APIConstants.apiUrl}price_estimation`,

                        data,

                        config

                    )


                /*
                |--------------------------------------------------------------------------
                | API error
                |--------------------------------------------------------------------------
                */

                if (
                    response.data?.error
                ) {

                    alert(
                        response.data.error
                    )

                    setShowLoading(false)

                    return

                }


                /*
                |--------------------------------------------------------------------------
                | Success
                |--------------------------------------------------------------------------
                */

                console.log(
                    'Price estimation response:',
                    response.data
                )


                setAllPackages(
                    response.data?.data || []
                )


                setDistance(
                    response.data?.km ||
                    calculatedDistance
                )


                setShowLoading(false)


                setShowModal(true)


            } catch (error) {

                console.error(
                    'Price estimation error:',
                    error
                )


                setShowLoading(false)


                alert(
                    'Unable to get price estimation. Please try again.'
                )

            }

        }


    /*
    |--------------------------------------------------------------------------
    | Render
    |--------------------------------------------------------------------------
    */

    return (

        <div>


            {showLoading && (
                <Loader />
            )}


            <PricingModal

                onCancel={() =>
                    setShowModal(false)
                }

                modalStatus={
                    showModal
                }

                modalData={
                    allPackages
                }

                distance={
                    distance
                }

            />


            <Header
                background="#06123C"
            />


            <div
                id="booking"
                className="section"
            >

                <div
                    className="section-center"
                >

                    <div
                        className="container mt-30"
                    >

                        <div
                            className="row"
                            style={{
                                marginTop: 180
                            }}
                        >


                            {/* ==========================================
                                RIGHT SIDE
                            ========================================== */}

                            <div
                                className="col-md-7 col-md-push-5"
                            >

                                <div
                                    className="booking-cta"
                                >

                                    <h1
                                        className="Responsive_p_p"
                                        style={{
                                            fontWeight:
                                                'bold',
                                            color:
                                                'white'
                                        }}
                                    >
                                        Get a Quotation!
                                    </h1>


                                    <p>
                                        Transparent pricing,
                                        no surprises.
                                    </p>


                                    <img
                                        src={
                                            ImageConstant.pricingBelow
                                        }
                                        style={{
                                            height: 300,
                                            width: 600,
                                            marginTop: 20,
                                            imageResolution:
                                                'from-image',
                                            objectFit:
                                                'contain'
                                        }}
                                    />

                                </div>

                            </div>


                            {/* ==========================================
                                FORM
                            ========================================== */}

                            <div
                                className="col-md-4 col-md-pull-7"
                            >

                                <div
                                    className="booking-form"
                                >

                                    <form
                                        onSubmit={
                                            reqDistance
                                        }
                                    >


                                        {/* ==================================
                                            PACKAGE TYPE
                                        ================================== */}

                                        <div
                                            className="form-group"
                                        >

                                            <span
                                                className="form-label"
                                            >
                                                Package Type
                                                {' '}
                                                <small>
                                                    (Optional)
                                                </small>
                                            </span>


                                            <input

                                                value={
                                                    packageData.packageType
                                                }

                                                onChange={(
                                                    event
                                                ) =>
                                                    setPackageData({

                                                        ...packageData,

                                                        packageType:
                                                            event.target.value

                                                    })
                                                }

                                                className="form-control"

                                                type="text"

                                                placeholder="Enter Package short Description"

                                            />

                                        </div>


                                        {/* ==================================
                                            PICKUP / DESTINATION
                                        ================================== */}

                                        <div
                                            className="row"
                                        >


                                            {/* PICKUP */}

                                            <div
                                                className="col-sm-6"
                                            >

                                                <div
                                                    className="form-group"
                                                >

                                                    <span
                                                        className="form-label"
                                                    >
                                                        Pickup
                                                    </span>


                                                    <PostalCodeInput

                                                        placeholder="Enter postal code"

                                                        onSelect={
                                                            handlePickupSelect
                                                        }

                                                    />


                                                    {pickupData.postalCode && (

                                                        <small
                                                            style={{
                                                                display:
                                                                    'block',

                                                                marginTop:
                                                                    5
                                                            }}
                                                        >

                                                            Selected:

                                                            {' '}

                                                            {
                                                                pickupData.postalCode
                                                            }

                                                        </small>

                                                    )}

                                                </div>

                                            </div>


                                            {/* DESTINATION */}

                                            <div
                                                className="col-sm-6"
                                            >

                                                <div
                                                    className="form-group"
                                                >

                                                    <span
                                                        className="form-label"
                                                    >
                                                        Destination
                                                    </span>


                                                    <PostalCodeInput

                                                        placeholder="Enter postal code"

                                                        onSelect={
                                                            handleDestinationSelect
                                                        }

                                                    />


                                                    {destinationData.postalCode && (

                                                        <small
                                                            style={{
                                                                display:
                                                                    'block',

                                                                marginTop:
                                                                    5
                                                            }}
                                                        >

                                                            Selected:

                                                            {' '}

                                                            {
                                                                destinationData.postalCode
                                                            }

                                                        </small>

                                                    )}

                                                </div>

                                            </div>

                                        </div>


                                        {/* ==================================
                                            WEIGHT
                                        ================================== */}

                                        <div
                                            className="row"
                                        >

                                            <div
                                                className="col-12"
                                            >

                                                <div
                                                    className="form-group"
                                                >

                                                    <span
                                                        className="form-label"
                                                    >
                                                        Weight
                                                    </span>


                                                    <input

                                                        type="number"

                                                        className="form-control"

                                                        value={
                                                            packageData.weight
                                                        }

                                                        onChange={(
                                                            event
                                                        ) =>
                                                            setPackageData({

                                                                ...packageData,

                                                                weight:
                                                                    event.target.value

                                                            })
                                                        }

                                                        placeholder="Enter weight"

                                                    />

                                                </div>

                                            </div>

                                        </div>


                                        {/* ==================================
                                            BUTTON
                                        ================================== */}

                                        <div
                                            style={{
                                                alignSelf:
                                                    'center',

                                                display:
                                                    'flex',

                                                alignContent:
                                                    'center',

                                                alignItems:
                                                    'center',

                                                justifyContent:
                                                    'center'
                                            }}
                                        >

                                            <button
                                                type="submit"
                                                className="btn"
                                            >
                                                Get Estimate
                                            </button>

                                        </div>


                                    </form>

                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            <Footer />

        </div>

    )

}